import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import axios from 'axios';
import { searchService } from '../../services/api';

// Axios raises a CanceledError (not AbortError) when the signal fires — detect
// both so the UI doesn't show a phantom "Rejected" when StrictMode or rapid
// keystrokes cancel the previous in-flight search.
const isCancel = (error) =>
  axios.isCancel?.(error) ||
  error?.name === 'AbortError' ||
  error?.name === 'CanceledError' ||
  error?.code === 'ERR_CANCELED';

// Request cancellation tokens
let searchController = null;
let suggestionsController = null;

// Simple in-memory cache for search results
const searchCache = new Map();
const CACHE_DURATION = 2 * 60 * 1000; // 2 minutes for fresher results

// Cache utilities
const getCacheKey = (query, modules, filters) => {
  return JSON.stringify({ query, modules, filters });
};

const getCachedResult = (key) => {
  const cached = searchCache.get(key);
  if (cached && Date.now() - cached.timestamp < CACHE_DURATION) {
    return cached.data;
  }
  searchCache.delete(key);
  return null;
};

const setCachedResult = (key, data) => {
  searchCache.set(key, {
    data,
    timestamp: Date.now()
  });
  
  // Clean old entries periodically (more efficient approach)
  if (searchCache.size > 100) { // Increased threshold to clean less frequently
    // Simple cleanup: remove entries older than 5 minutes
    const now = Date.now();
    const cutoff = now - CACHE_DURATION;
    
    for (const [cacheKey, cacheValue] of searchCache.entries()) {
      if (cacheValue.timestamp < cutoff) {
        searchCache.delete(cacheKey);
      }
    }
    
    // If still too many entries, remove oldest 20
    if (searchCache.size > 80) {
      const oldestEntries = Array.from(searchCache.entries())
        .sort(([,a], [,b]) => a.timestamp - b.timestamp)
        .slice(0, 20);
      oldestEntries.forEach(([cacheKey]) => searchCache.delete(cacheKey));
    }
  }
};

// Async thunks for search operations
export const performSearch = createAsyncThunk(
  'search/performSearch',
  async ({ query, modules, filters, searchType, limit, offset, useCache = true }, { rejectWithValue }) => {
    try {
      // Cancel previous search request
      if (searchController) {
        searchController.abort();
      }
      
      // Check cache for exact matches (only for new searches, not pagination)
      if (useCache && offset === 0) {
        const cacheKey = getCacheKey(query, modules, filters);
        const cachedResult = getCachedResult(cacheKey);
        if (cachedResult) {
          return { ...cachedResult, cached: true };
        }
      }
      
      // Create new abort controller
      searchController = new AbortController();
      
      const params = {
        q: query,
        modules: Array.isArray(modules) ? modules.join(',') : modules,
        type: searchType || 'hybrid',
        limit: limit || 20,
        offset: offset || 0,
        cache: useCache !== false ? 'true' : 'false',
        ...filters
      };
      
      const response = await searchService.search(params, {
        signal: searchController.signal
      });
      
      // Cache the result for new searches
      if (offset === 0 && useCache) {
        const cacheKey = getCacheKey(query, modules, filters);
        setCachedResult(cacheKey, response.data);
      }
      
      return response.data;
    } catch (error) {
      if (isCancel(error)) {
        return rejectWithValue({ cancelled: true });
      }
      const data = error.response?.data;
      const message =
        (data && typeof data === 'object' && (data.error || data.detail)) ||
        (typeof data === 'string' ? null : null) ||
        error.message ||
        'Search failed';
      return rejectWithValue({ error: message });
    }
  }
);

export const performAdvancedSearch = createAsyncThunk(
  'search/performAdvancedSearch',
  async (searchData, { rejectWithValue }) => {
    try {
      const response = await searchService.searchAdvanced(searchData);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

export const getSuggestions = createAsyncThunk(
  'search/getSuggestions',
  async (partialQuery, { rejectWithValue }) => {
    try {
      if (!partialQuery || partialQuery.length < 2) {
        return { suggestions: [] };
      }
      
      // Cancel previous suggestions request
      if (suggestionsController) {
        suggestionsController.abort();
      }
      
      // Create new abort controller
      suggestionsController = new AbortController();
      
      const response = await searchService.getSuggestions(partialQuery, {
        signal: suggestionsController.signal
      });
      return response.data;
    } catch (error) {
      if (isCancel(error)) {
        return rejectWithValue({ cancelled: true });
      }
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

export const getFacets = createAsyncThunk(
  'search/getFacets',
  async ({ query, modules }, { rejectWithValue }) => {
    try {
      const params = {};
      if (query) params.q = query;
      if (modules) params.modules = Array.isArray(modules) ? modules.join(',') : modules;
      
      const response = await searchService.getFacets(params);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

export const getSearchHealth = createAsyncThunk(
  'search/getSearchHealth',
  async (_, { rejectWithValue }) => {
    try {
      const response = await searchService.getSearchHealth();
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

// Load persisted search history from the server (called once on app init).
export const loadSearchHistory = createAsyncThunk(
  'search/loadSearchHistory',
  async (_, { rejectWithValue }) => {
    try {
      const response = await searchService.getSearchHistory();
      return response.data?.search_history || [];
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

// Persist history to UserNavigationPreference. Callers debounce to avoid thrashing.
export const saveSearchHistory = createAsyncThunk(
  'search/saveSearchHistory',
  async (history, { rejectWithValue }) => {
    try {
      await searchService.saveSearchHistory(history);
      return history;
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

// Initial state
const initialState = {
  // Search query and settings
  query: '',
  modules: ['core', 'mm', 'crm'],
  searchType: 'hybrid',
  filters: {},
  
  // Search results
  results: [],
  totalFound: 0,
  currentPage: 1,
  pageSize: 20,
  hasMore: false,
  
  // UI state
  isSearching: false,
  isLoadingMore: false,
  error: null,
  
  // Search suggestions
  suggestions: [],
  isSuggestionsLoading: false,
  showSuggestions: false,
  
  // Search facets
  facets: {},
  isFacetsLoading: false,
  
  // Search history
  searchHistory: [],
  
  // Performance and metadata
  searchMetadata: null,
  searchPerformance: null,
  
  // Search health
  searchHealth: null,
  
  // Advanced search
  advancedSearchOpen: false,
  savedSearches: [],
};

// Search slice
const searchSlice = createSlice({
  name: 'search',
  initialState,
  reducers: {
    // Query management
    setQuery: (state, action) => {
      state.query = action.payload;
    },
    clearQuery: (state) => {
      state.query = '';
      state.suggestions = [];
      state.showSuggestions = false;
    },
    
    // Search settings
    setModules: (state, action) => {
      state.modules = action.payload;
    },
    setSearchType: (state, action) => {
      state.searchType = action.payload;
    },
    setFilters: (state, action) => {
      state.filters = { ...state.filters, ...action.payload };
    },
    clearFilters: (state) => {
      state.filters = {};
    },
    removeFilter: (state, action) => {
      delete state.filters[action.payload];
    },
    
    // Results management
    clearResults: (state) => {
      state.results = [];
      state.totalFound = 0;
      state.currentPage = 1;
      state.hasMore = false;
      state.searchMetadata = null;
      state.searchPerformance = null;
    },
    setCurrentPage: (state, action) => {
      state.currentPage = action.payload;
    },
    
    // UI state
    clearError: (state) => {
      state.error = null;
    },
    
    // Suggestions
    setSuggestions: (state, action) => {
      state.suggestions = action.payload;
    },
    setShowSuggestions: (state, action) => {
      state.showSuggestions = action.payload;
    },
    
    // Search history
    addToHistory: (state, action) => {
      const { query, modules, filters } = action.payload;
      const historyItem = {
        query,
        modules,
        filters,
        timestamp: new Date().toISOString(),
        id: Date.now().toString()
      };
      
      // Remove duplicates and add to front
      state.searchHistory = [
        historyItem,
        ...state.searchHistory.filter(item => item.query !== query).slice(0, 9) // Keep last 10
      ];
    },
    removeFromHistory: (state, action) => {
      state.searchHistory = state.searchHistory.filter(item => item.id !== action.payload);
    },
    clearHistory: (state) => {
      state.searchHistory = [];
    },
    
    // Advanced search
    setAdvancedSearchOpen: (state, action) => {
      state.advancedSearchOpen = action.payload;
    },
    
    // Saved searches
    addSavedSearch: (state, action) => {
      state.savedSearches.push({
        ...action.payload,
        id: Date.now().toString(),
        createdAt: new Date().toISOString()
      });
    },
    removeSavedSearch: (state, action) => {
      state.savedSearches = state.savedSearches.filter(search => search.id !== action.payload);
    },
  },
  extraReducers: (builder) => {
    // Perform Search
    builder
      .addCase(performSearch.pending, (state, action) => {
        if (action.meta.arg.offset === 0) {
          state.isSearching = true;
          state.error = null;
        } else {
          state.isLoadingMore = true;
        }
      })
      .addCase(performSearch.fulfilled, (state, action) => {
        state.isSearching = false;
        state.isLoadingMore = false;
        state.error = null;

        const { results, metadata, performance } = action.payload;
        
        if (action.meta.arg.offset === 0) {
          // New search
          state.results = results;
          state.currentPage = 1;
        } else {
          // Load more results
          state.results = [...state.results, ...results];
          state.currentPage += 1;
        }
        
        state.totalFound = metadata.total_found;
        state.hasMore = metadata.has_more;
        state.searchMetadata = metadata;
        state.searchPerformance = performance;
        
        // Add to search history for new searches
        if (action.meta.arg.offset === 0 && action.meta.arg.query) {
          searchSlice.caseReducers.addToHistory(state, {
            payload: {
              query: action.meta.arg.query,
              modules: action.meta.arg.modules || state.modules,
              filters: action.meta.arg.filters || state.filters
            }
          });
        }
      })
      .addCase(performSearch.rejected, (state, action) => {
        // Ignore cancellations — a follow-up search already replaced this one.
        if (action.payload?.cancelled) return;
        state.isSearching = false;
        state.isLoadingMore = false;
        state.error = action.payload?.error || 'Search failed';
      });
    
    // Advanced Search
    builder
      .addCase(performAdvancedSearch.pending, (state) => {
        state.isSearching = true;
        state.error = null;
      })
      .addCase(performAdvancedSearch.fulfilled, (state, action) => {
        state.isSearching = false;
        state.error = null;

        const { results, metadata, performance } = action.payload;
        state.results = results;
        state.totalFound = metadata.total_found;
        state.hasMore = metadata.has_more;
        state.searchMetadata = metadata;
        state.searchPerformance = performance;
        state.currentPage = 1;
      })
      .addCase(performAdvancedSearch.rejected, (state, action) => {
        state.isSearching = false;
        state.error = action.payload?.error || action.error?.message || 'Advanced search failed';
      });
    
    // Get Suggestions
    builder
      .addCase(getSuggestions.pending, (state) => {
        state.isSuggestionsLoading = true;
      })
      .addCase(getSuggestions.fulfilled, (state, action) => {
        state.isSuggestionsLoading = false;
        state.suggestions = action.payload.suggestions || [];
        state.showSuggestions = state.suggestions.length > 0;
      })
      .addCase(getSuggestions.rejected, (state) => {
        state.isSuggestionsLoading = false;
        state.suggestions = [];
        state.showSuggestions = false;
      });
    
    // Get Facets
    builder
      .addCase(getFacets.pending, (state) => {
        state.isFacetsLoading = true;
      })
      .addCase(getFacets.fulfilled, (state, action) => {
        state.isFacetsLoading = false;
        state.facets = action.payload.facets || {};
      })
      .addCase(getFacets.rejected, (state) => {
        state.isFacetsLoading = false;
        state.facets = {};
      });
    
    // Get Search Health
    builder
      .addCase(getSearchHealth.fulfilled, (state, action) => {
        state.searchHealth = action.payload;
      });

    // Load persisted search history
    builder
      .addCase(loadSearchHistory.fulfilled, (state, action) => {
        state.searchHistory = action.payload || [];
      });
  },
});

// Export actions
export const {
  setQuery,
  clearQuery,
  setModules,
  setSearchType,
  setFilters,
  clearFilters,
  removeFilter,
  clearResults,
  setCurrentPage,
  clearError,
  setSuggestions,
  setShowSuggestions,
  addToHistory,
  removeFromHistory,
  clearHistory,
  setAdvancedSearchOpen,
  addSavedSearch,
  removeSavedSearch,
} = searchSlice.actions;

// Selectors
export const selectSearch = (state) => state.search;
export const selectSearchResults = (state) => state.search.results;
export const selectSearchQuery = (state) => state.search.query;
export const selectSearchModules = (state) => state.search.modules;
export const selectSearchType = (state) => state.search.searchType;
export const selectSearchFilters = (state) => state.search.filters;
export const selectIsSearching = (state) => state.search.isSearching;
export const selectSearchError = (state) => state.search.error;
export const selectSearchSuggestions = (state) => state.search.suggestions;
export const selectSearchFacets = (state) => state.search.facets;
export const selectSearchHistory = (state) => state.search.searchHistory;
export const selectSearchMetadata = (state) => state.search.searchMetadata;
export const selectSearchPerformance = (state) => state.search.searchPerformance;
export const selectSearchHealth = (state) => state.search.searchHealth;

export default searchSlice.reducer;